import React from "react";
import { useQuery } from "react-query";
import { fetchApi } from "../../helpers/reactQueryApi";
import classNames from "classnames";
import { matchPath, useLocation } from "../../helpers/tixxt-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@fortawesome/pro-solid-svg-icons";

type GroupNavigationItem = {
  text: string;
  link: string;
  new_window?: boolean;
  join_request_count?: number;
  navigation_items?: {
    text: string;
    link: string;
    new_window?: boolean;
  }[];
  active_match?: string;
  // "Beitrag verfassen" link of a hidden inline composer (#649), web only
  compose_activity?: boolean;
};

function useGroupNavigationItems(groupSlug: string | null | undefined) {
  return useQuery<GroupNavigationItem[]>(
    ["groupNavigationItems", groupSlug],
    () => fetchApi(`/groups/${groupSlug}/navigation`),
    {
      enabled: !!groupSlug && groupSlug !== "new",
      refetchOnWindowFocus: false,
    },
  );
}

/* Use this if you want to pass the groupSlug in as a prop */
export default function GroupNavigation({
  groupSlug,
}: {
  groupSlug?: string | null;
}) {
  const { isLoading, data } = useGroupNavigationItems(groupSlug);
  const { pathname } = useLocation();

  if (!groupSlug) return null;

  // The app keeps its native posting button (#649)
  const items = window.isApp
    ? data?.filter((nav) => !nav.compose_activity)
    : data;

  // The most specific item wins: when an item links to exactly this page,
  // broader matches stay inactive — e.g. "Beitrag verfassen" lies below the
  // "Beiträge" pattern /groups/:group/activitystreams/* (#649).
  const exactIndex = items?.findIndex((nav) => nav.link === pathname) ?? -1;

  return (
    <ul className={"group-navigation mb-8 flex flex-wrap flex-stretch"}>
      {isLoading
        ? [
            I18n.t("js.groups.navigation.activities"),
            I18n.t("js.groups.navigation.members"),
            I18n.t("js.groups.navigation.calendar"),
            I18n.t("js.groups.navigation.tasks"),
            I18n.t("js.groups.navigation.files"),
            I18n.t("js.groups.navigation.profile"),
          ].map((text, i) => <Placeholder key={i}>{text}</Placeholder>)
        : items?.map((nav, i) => (
            <NavigationItem
              key={i}
              last={i === items.length - 1}
              active={
                exactIndex >= 0 ? i === exactIndex : matchesPath(nav, pathname)
              }
              {...nav}
            />
          ))}
      <li className={"flex-1 border-b border-neutral"} />
    </ul>
  );
}

function matchesPath(
  { link, active_match }: GroupNavigationItem,
  pathname: string,
): boolean {
  // active_match provided? use this to match
  return active_match
    ? matchPath(active_match, pathname)
    : // Link looks like a module link? Match on prefix, equality otherwise
      link.match(/^\/groups\/.+?\//)
      ? pathname.indexOf(link) === 0
      : pathname === link;
}

function NavigationItem({
  text,
  link,
  new_window,
  navigation_items,
  last,
  join_request_count,
  active: isActive,
}: GroupNavigationItem & { last?: boolean; active: boolean }) {
  const hasNavigationItems = navigation_items && navigation_items.length > 0;

  return (
    <li
      className={classNames("flex border-b border-neutral h-8", {
        "pr-4": !last,
        dropdown: hasNavigationItems,
      })}
    >
      <a
        className={classNames("muted flex items-center text-sm", {
          "border-b-2 border-primary font-semibold -mb-px": isActive,
        })}
        href={link}
        data-bs-toggle={hasNavigationItems ? "dropdown" : null}
        target={new_window ? "_blank" : undefined}
        rel="noreferrer"
      >
        {text}
        {hasNavigationItems && (
          <FontAwesomeIcon icon={faCaretDown} size="xs" className="ml-1" />
        )}
      </a>
      {hasNavigationItems && (
        <ul className="dropdown-menu">
          {navigation_items.map(({ text, link, new_window }, i) => (
            <li key={i}>
              <a
                className="dropdown-item"
                href={link}
                target={new_window ? "_blank" : undefined}
                rel="noreferrer"
              >
                {text}
              </a>
            </li>
          ))}
        </ul>
      )}
      {join_request_count ? (
        <span className="ml-1 badge rounded-xl self-center bg-unread text-[10px]">
          {join_request_count}
        </span>
      ) : null}
    </li>
  );
}

function Placeholder({ children }: { children?: React.ReactNode }) {
  return (
    <li className={"flex border-b border-neutral pr-4 h-8"}>
      <span className="flex items-center text-sm">
        <span className={"inline-block bg-gray-300 animate-pulse rounded-sm"}>
          <span className={"invisible"}>{children || "&nbsp;"}</span>
        </span>
      </span>
    </li>
  );
}
